// Shared visual tokens for Recharts-based charts. Centralised here so the
// KPI compliance chart, KPI monthly drawer chart, the performance history
// chart and the report charts can't drift in colour, height, animation
// timing, or reduced-motion behaviour.
//
// Two palettes live here and they are not interchangeable. The brand violet
// primary marks the single metric the reader is looking at. The outcome
// series below carry semantic meaning — met, slipped, failed — and are the
// only correct choice for a breakdown chart, where violet would claim one
// outcome is the subject and the others are context.

import { useEffect, useState } from "react";

/** Standard chart container height used across the manage surfaces. */
export const CHART_HEIGHT = 200;

/** Brand-violet primary line — deliberately identical in light + dark
 *  modes; the violet primary is the brand anchor and must not shift. */
export const PRIMARY_LINE_COLOUR = "#7447d1";

/** The card surface a chart sits on. Drawn as a hairline between adjacent
 *  fills, it reads as a gap rather than as a border, which is what separates
 *  two semantic colours that are close in luminance — red against green
 *  measures barely over 1:1 for a reader with deuteranopia, so the boundary
 *  has to come from the surface rather than from the fills. */
export const CHART_SURFACE_COLOUR_LIGHT = "#ffffff";
export const CHART_SURFACE_COLOUR_DARK = "#1e293b";

/** Outcome series, from the brand palette's success / warning / error rows.
 *  Emerald reads as met, amber as slipped, red as failed. */
export const SUCCESS_SERIES_COLOUR_LIGHT = "#059669";
export const SUCCESS_SERIES_COLOUR_DARK = "#34d399";
export const WARNING_SERIES_COLOUR_LIGHT = "#d97706";
export const WARNING_SERIES_COLOUR_DARK = "#fbbf24";
export const ERROR_SERIES_COLOUR_LIGHT = "#dc2626";
export const ERROR_SERIES_COLOUR_DARK = "#f87171";

export const TARGET_COLOUR_LIGHT = "#94a3b8";
export const TARGET_COLOUR_DARK = "#64748b";

export const GRID_COLOUR_LIGHT = "rgba(148, 163, 184, 0.15)";
export const GRID_COLOUR_DARK = "rgba(148, 163, 184, 0.08)";

export const TICK_COLOUR_LIGHT = "#64748b";
export const TICK_COLOUR_DARK = "#94a3b8";

export const TICK_FONT_SIZE = 11;

/** Recharts `strokeDasharray` value for dashed reference lines. */
export const REFERENCE_LINE_DASH = "4 4";

/** Default animation duration in ms when reduced-motion is not requested. */
export const DEFAULT_ANIMATION_DURATION_MS = 400;

export function getChartSurfaceColour(isDark: boolean): string {
  return isDark ? CHART_SURFACE_COLOUR_DARK : CHART_SURFACE_COLOUR_LIGHT;
}

export function getSuccessSeriesColour(isDark: boolean): string {
  return isDark ? SUCCESS_SERIES_COLOUR_DARK : SUCCESS_SERIES_COLOUR_LIGHT;
}

export function getWarningSeriesColour(isDark: boolean): string {
  return isDark ? WARNING_SERIES_COLOUR_DARK : WARNING_SERIES_COLOUR_LIGHT;
}

export function getErrorSeriesColour(isDark: boolean): string {
  return isDark ? ERROR_SERIES_COLOUR_DARK : ERROR_SERIES_COLOUR_LIGHT;
}

export function getTargetColour(isDark: boolean): string {
  return isDark ? TARGET_COLOUR_DARK : TARGET_COLOUR_LIGHT;
}

export function getGridColour(isDark: boolean): string {
  return isDark ? GRID_COLOUR_DARK : GRID_COLOUR_LIGHT;
}

export function getTickColour(isDark: boolean): string {
  return isDark ? TICK_COLOUR_DARK : TICK_COLOUR_LIGHT;
}

/** Recharts `Tooltip.contentStyle` matching the existing KPI tooltip card. */
export function getTooltipContentStyle(isDark: boolean): {
  background: string;
  border: string;
  borderRadius: number;
  fontSize: number;
  color: string;
} {
  return {
    background: isDark ? "#1e293b" : "#ffffff",
    border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
    borderRadius: 6,
    fontSize: 12,
    color: isDark ? "#f1f5f9" : "#0f172a",
  };
}

function usePrefersReducedMotion(): boolean {
  // Lazy initialiser reads the current media-query state synchronously on
  // mount. The effect only sets up the subscription; setState inside the
  // subscription callback is the allowed pattern.
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = () => setReduced(mq.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduced;
}

/** Returns the animation duration in ms — 0 when the user has requested
 *  reduced motion, otherwise `DEFAULT_ANIMATION_DURATION_MS`. Pair with
 *  Recharts' `isAnimationActive={duration !== 0}` + `animationDuration={duration}`
 *  so screen-reader and motion-sensitivity preferences are respected. */
export function useReducedMotionAwareAnimationDuration(): number {
  const reduced = usePrefersReducedMotion();
  return reduced ? 0 : DEFAULT_ANIMATION_DURATION_MS;
}
